import { getUserId } from "./basicPlanAccess";

const COMPLETED_LESSONS_STORAGE_PREFIX = "completedLessons";

const getStorageKey = (user) => {
  const userId = getUserId(user) || "anonymous";
  return `${COMPLETED_LESSONS_STORAGE_PREFIX}:${userId}`;
};

const canUseStorage = () =>
  typeof window !== "undefined" && window.localStorage;

/**
 * Get the list of completed lesson IDs for a user from storage.
 * @param {object} user - Redux user object
 * @returns {string[]} Array of completed lesson ID strings
 */
export const getCompletedLessons = (user) => {
  if (!canUseStorage()) return [];

  const storageKey = getStorageKey(user);
  if (!storageKey) return [];

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (_error) {
    return [];
  }
};

/**
 * Check if a specific lesson ID is marked as completed.
 * Matches both "123", "v2-123", and numeric equivalents.
 * @param {object} user - Redux user object
 * @param {string|number} lessonId - Lesson ID or pk
 * @returns {boolean}
 */
export const isLessonCompleted = (user, lessonId) => {
  if (lessonId === null || lessonId === undefined || lessonId === "") {
    return false;
  }

  const completedList = getCompletedLessons(user);
  const targetRaw = String(lessonId).trim();
  const targetClean = targetRaw.replace(/^v2-/, "");

  return completedList.some((id) => {
    const itemRaw = String(id).trim();
    const itemClean = itemRaw.replace(/^v2-/, "");
    return (
      itemRaw === targetRaw ||
      itemClean === targetClean ||
      (Number(itemClean) > 0 && Number(itemClean) === Number(targetClean))
    );
  });
};

/**
 * Mark a lesson as completed for the current user in local storage.
 * @param {object} user - Redux user object
 * @param {string|number} lessonId - Lesson ID or pk
 */
export const markLessonCompleted = (user, lessonId) => {
  if (!canUseStorage() || lessonId === null || lessonId === undefined || lessonId === "") {
    return;
  }

  const storageKey = getStorageKey(user);
  if (!storageKey) return;

  const existing = getCompletedLessons(user);
  const normalizedId = String(lessonId).trim();

  if (!existing.includes(normalizedId)) {
    const nextList = [...existing, normalizedId];
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(nextList));
    } catch (_error) {
      // Ignore storage write errors
    }
  }
};
